import microuniversoApi from "app/api/microuniversoApi";
import { useAppSelector } from "app/redux/hooks";
import { IOrcamento } from "app/types/IOrcamento";
import { Parametro, ParametroPorItem } from "app/types/Parametro";
import { DadosResumoOrcamento, ObterResumoOrcamento } from "app/types/Resumo";
import { FormikErrors } from "formik";
import { tratamentoErroPadrao } from "app/use-cases";
import { obterPermissao } from "app/use-cases/obterPermissao";
import { atualizarOrcamentoResumo } from "app/use-cases/orcamento/atualizarOrcamentoResumo";
import { obterParametros, obterParametrosPorOidItem } from "app/use-cases/parametro/obterParametros";
import globalMU from "globalMU";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { Breadcrumb2Container } from "../breadcrumb2/Breadcrumb2Container";
import { ResumoOrcamento } from "./ResumoOrcamento";
import store from "app/redux/store/store";
import { gravarTelasAlteradasSalvaAction } from "app/redux/telaOrcamentoAlteradaNaoSalvaSlice";

export const ResumoOrcamentoContainer = memo(() => {
    const isMounted = useRef<boolean>(true)
    const orcamento: IOrcamento = useAppSelector((store) => store.orcamento);
    const orcamentoRef = useRef(orcamento)
    orcamentoRef.current = orcamento
    const [dadosResumoOrcamento, setDadosResumoOrcamento] = useState<DadosResumoOrcamento>(null);
    const [permitirAreaVenda, setPermitirAreaVenda] = useState<boolean>(null);
    const [permitirAlterarTPO, setPermitirAlterarTPO] = useState<boolean>(null);
    const [parametrosResumoOrcamento, setParametrosResumoOrcamento] = useState<Parametro[]>([]);
    const [parametrosPorFilial, setParametrosPorFilial] = useState<ParametroPorItem[]>([]);
    const [processando, setProcessando] = useState<boolean>(true);


    // Função para buscar todos os dados necessários
    const buscarDados = async () => {
        try {
            globalMU.blockUI();
            setProcessando(true);

            // Executa todas as requisições em paralelo
            const [resumo, parametros, parametrosFilial, permissaoAreaVenda, permissaoAlterarTPO] = await Promise.all([
                microuniversoApi.Orcamento.obterDadosResumo(orcamento.IdentificadorOrcamento),
                obterParametros({
                    nomes: [
                        "DEFINIR_PREVISAO_ENTREGA_POR_ITEM",
                        "ALTERA_TPO_ORCAMENTO"
                    ]
                }),
                obterParametrosPorOidItem({
                    nomes: [
                        "VENDA_COM_ENTREGA_POSTERIOR",
                        "UTILIZA_RELACIONAMENTO_FILIAL_VENDEDOR_FONTE_RECEITA", // 487678
                    ],
                    oidItem: orcamento.OidFilialOrcamento
                }),
                permitidoAcaoFuncionalidade("ORCAMENTO_RESUMO", "MOSTRAR_AREA_VENDA"),
                permitidoAcaoFuncionalidade("ORCAMENTO_RESUMO", "PERMITIR_ALTERAR_TPO")
            ]);

            // Atualiza os estados apenas se o componente estiver montado
            if (isMounted.current) {
                setDadosResumoOrcamento({
                    ...resumo,
                    ObterResumoOrcamento: resumo.ObterResumoOrcamento,
                    PedidoCompraCliente: resumo.PedidoCompraCliente
                });
                setParametrosResumoOrcamento(parametros);
                setParametrosPorFilial(parametrosFilial);
                setPermitirAreaVenda(permissaoAreaVenda);
                setPermitirAlterarTPO(permissaoAlterarTPO && parametros.find(p => p.Nome === "ALTERA_TPO_ORCAMENTO")?.Valor === "S");
            }
        } catch (e) {
            tratamentoErroPadrao(e);
        } finally {
            if (isMounted.current) setProcessando(false);
            globalMU.unblockUI();
        }
    };

    // Função para salvar o resumo do orçamento
    const onSalvarResumoOrcamento = useCallback(async (dados: ObterResumoOrcamento) => {
        try {
            globalMU.blockUI();
            setProcessando(true);

            const orcamentoModificado = {
                ...orcamentoRef.current,
                Situacao: dados.Situacao,
                FinalidadeVenda: dados.FinalidadeVenda,
                DataValidade: dados.DataValidade,
                PedidoCompraCliente: dados.PedidoCompraCliente,
                TipoOperacao: dados.TipoOperacao,
                ObservacaoNF: dados.ObservacaoNF,
                ObservacaoOrcamento: dados.ObservacaoOrcamento,
                UsarObservacaoTextoLivreNF: dados.UsarObservacaoTextoLivreNF
            };

            await atualizarOrcamentoResumo({ orcamento: orcamentoModificado });

            if (isMounted.current) {
                store.dispatch(gravarTelasAlteradasSalvaAction("telaResumoOrcamento"));
            }
        } catch (e) {
            tratamentoErroPadrao(e);
        } finally {
            if (isMounted.current) setProcessando(false);
            globalMU.unblockUI();
        }
    }, [])

    const onErrorValidacao = useCallback((erros: FormikErrors<DadosResumoOrcamento>) => {
        const primeiroErro = Object.values(erros).find(e => typeof e === 'string') as string | undefined;
        globalMU.showErrorMessage(primeiroErro ?? "Verifique os campos obrigatórios")
    }, [])

    // Função para verificar permissões
    const permitidoAcaoFuncionalidade = async (siglaFuncionalidade: string, acao?: string) =>
        await obterPermissao({ funcionalidade: siglaFuncionalidade, acao });

    // Função para obter o valor de um parâmetro
    const valorParametro = (nome: string) => parametrosResumoOrcamento.find(p => p.Nome === nome)?.Valor;
    const valorParametroPorFilial = (nome: string) => parametrosPorFilial.find(p => p.Nome === nome)?.Valor;

    useEffect(() => () => { isMounted.current = false }, [])

    // Executa a busca de dados quando o orçamento é carregado
    useEffect(() => {
        if (orcamento?.Numero != null) {
            buscarDados();
        }
    }, [orcamento?.Numero]);
    
    return (
        <div 
            key={`resumo_orcamento_${orcamento?.IdentificadorOrcamento || 'loading'}`}
            id={`resumo_orcamento_${orcamento?.IdentificadorOrcamento || 'loading'}`}
        
        >
            {orcamento != null && permitirAreaVenda != null && permitirAlterarTPO != null
                && parametrosPorFilial != undefined && parametrosPorFilial.length > 0
                && parametrosResumoOrcamento != undefined && parametrosResumoOrcamento.length > 0
                && dadosResumoOrcamento != undefined && dadosResumoOrcamento.ObterResumoOrcamento != null && (
                    <>
                        <Breadcrumb2Container
                            key="resumo_breadcrumb"
                            passos={["Dados do Orçamento"]}
                            exibirBotaoLimpar={false}
                            exibirBotaoVoltar={false}
                            processando={processando}
                            id="ResumoOrcamentoContainer_Breadcrumb"
                        />

                        <br />

                        <ResumoOrcamento
                            key={`resumo_content_${orcamento.IdentificadorOrcamento}`}
                            somenteLeitura={orcamento.SomenteLeitura}
                            processando={processando}
                            dados={dadosResumoOrcamento}
                            parametrosPorFilial={parametrosPorFilial} // 487678
                            tipoOrcamentoDescricao={orcamento.TipoOrcamentoDescricao}
                            orcamentoConfirmado={dadosResumoOrcamento.SituacoesOrcamento?.some(x => x.Codigo === 1 && x.Descricao.toUpperCase() === "ORÇAMENTO CONFIRMADO")}
                            apenasConfirmado={dadosResumoOrcamento.SituacoesOrcamento?.some(x => x.Codigo === 1 && x.Descricao.toUpperCase() === "APENAS CONFIRMADO")}
                            enviarParaSupervisao={dadosResumoOrcamento.SituacoesOrcamento?.some(x => x.Codigo === 5)}
                            apenasOrcamento={dadosResumoOrcamento.SituacoesOrcamento?.some(x => x.Codigo === 6)}
                            permitirAlterarTPO={permitirAlterarTPO}
                            permitirMostrarAreaVenda={permitirAreaVenda}
                            permitirPrevisaoEntregaPorItem={valorParametro("DEFINIR_PREVISAO_ENTREGA_POR_ITEM") === "S"}
                            permitirVendaComEntregaPosterior={valorParametroPorFilial("VENDA_COM_ENTREGA_POSTERIOR") === "S"}
                            onAplicar={onSalvarResumoOrcamento}
                            onErrorValidacao={onErrorValidacao}
                        />
                    </>
                )}
        </div>
    );
})